import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { logger } from '../utils/logger';
import {
  Database,
  Trash2,
  Archive,
  RefreshCcw,
  AlertTriangle,
  CheckCircle,
  Clock,
  MessageSquare,
  Users,
  Activity,
  HardDrive,
  Shield
} from 'lucide-react';

const Maintenance = ({ agent }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleanupDays, setCleanupDays] = useState(30);
  const [archiveDays, setArchiveDays] = useState(7);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [actionResult, setActionResult] = useState(null);

  const fetchStats = async () => {
    if (!agent?.id) return;

    setLoading(true);
    try {
      const response = await api.get(`/api/sessions/stats?requester_id=${agent.id}`);
      setStats(response.data);
    } catch (error) {
      logger.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [agent?.id]);

  const handlePreviewCleanup = async () => {
    try {
      const response = await api.delete(
        `/api/sessions/cleanup?requester_id=${agent.id}&days_old=${cleanupDays}&dry_run=true`
      );
      setCleanupPreview(response.data);
      setActionResult(null);
    } catch (error) {
      logger.error('Error previewing cleanup:', error);
      setActionResult({ type: 'error', message: error.response?.data?.detail || 'Failed to preview cleanup' });
    }
  };

  const handleConfirmCleanup = async () => {
    if (!cleanupPreview || cleanupPreview.sessions_to_delete === 0) return;

    setIsDeleting(true);
    try {
      const response = await api.delete(
        `/api/sessions/cleanup?requester_id=${agent.id}&days_old=${cleanupDays}&dry_run=false`
      );
      setActionResult({ type: 'success', message: response.data.message });
      setCleanupPreview(null);
      fetchStats();
    } catch (error) {
      logger.error('Error during cleanup:', error);
      setActionResult({ type: 'error', message: error.response?.data?.detail || 'Cleanup failed' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleArchive = async () => {
    setIsArchiving(true);
    try {
      const response = await api.post(`/api/sessions/archive`, {
        requester_id: agent.id,
        days_old: archiveDays
      });
      setActionResult({ type: 'success', message: response.data.message });
      fetchStats();
    } catch (error) {
      logger.error('Error archiving sessions:', error);
      setActionResult({ type: 'error', message: error.response?.data?.detail || 'Archive failed' });
    } finally {
      setIsArchiving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCcw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-purple-100 rounded-xl">
            <Database className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">System Maintenance</h2>
            <p className="text-sm text-gray-500">Manage database storage and cleanup old sessions</p>
          </div>
        </div>
        <Button onClick={fetchStats} variant="outline" className="flex items-center gap-2">
          <RefreshCcw className="w-4 h-4" />
          Refresh Stats
        </Button>
      </div>

      {/* Action Result Alert */}
      {actionResult && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          actionResult.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          {actionResult.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-red-600" />
          )}
          <span className={actionResult.type === 'success' ? 'text-green-800' : 'text-red-800'}>
            {actionResult.message}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={() => setActionResult(null)}
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Sessions</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.total_sessions || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Now</p>
                <p className="text-3xl font-bold text-green-600">{stats?.active_sessions || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Activity className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Messages</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.total_messages || 0}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <MessageSquare className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Old Sessions (30d+)</p>
                <p className="text-3xl font-bold text-orange-600">{stats?.old_sessions_30_days || 0}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="w-5 h-5" />
            Session Status Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Active (AI)</p>
              <p className="text-2xl font-bold">{stats?.status_breakdown?.active || 0}</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-gray-500">Waiting for Agent</p>
              <p className="text-2xl font-bold text-yellow-600">{stats?.status_breakdown?.waiting_for_agent || 0}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-500">Agent Active</p>
              <p className="text-2xl font-bold text-green-600">{stats?.status_breakdown?.agent_active || 0}</p>
            </div>
            <div className="p-4 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-500">Closed</p>
              <p className="text-2xl font-bold text-gray-600">{stats?.status_breakdown?.closed || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cleanup Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Delete Old Sessions */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />
              Delete Old Sessions
            </CardTitle>
            <CardDescription>
              Permanently delete sessions and their messages older than specified days.
              This action cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="text-sm text-gray-600 whitespace-nowrap">Delete sessions older than:</label>
              <Input
                type="number"
                min="1"
                max="365"
                value={cleanupDays}
                onChange={(e) => {
                  setCleanupDays(parseInt(e.target.value) || 30);
                  setCleanupPreview(null);
                }}
                className="w-24"
              />
              <span className="text-sm text-gray-600">days</span>
            </div>

            {cleanupPreview && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-2">
                <div className="flex items-center gap-2 text-red-700 font-medium">
                  <AlertTriangle className="w-4 h-4" />
                  Preview Results
                </div>
                <p className="text-sm text-red-600">
                  <strong>{cleanupPreview.sessions_to_delete}</strong> sessions and{' '}
                  <strong>{cleanupPreview.messages_to_delete}</strong> messages will be deleted.
                </p>
                <p className="text-xs text-red-500">
                  Cutoff date: {new Date(cleanupPreview.cutoff_date).toLocaleDateString()}
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handlePreviewCleanup}
                variant="outline"
                className="flex-1"
              >
                Preview
              </Button>
              <Button
                onClick={handleConfirmCleanup}
                variant="destructive"
                className="flex-1"
                disabled={!cleanupPreview || cleanupPreview.sessions_to_delete === 0 || isDeleting}
              >
                {isDeleting ? (
                  <>
                    <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Sessions
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Archive Closed Sessions */}
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Archive className="w-5 h-5" />
              Archive Closed Sessions
            </CardTitle>
            <CardDescription>
              Mark old closed sessions as archived. They will remain in the database but
              can be filtered out from the main view.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="text-sm text-gray-600 whitespace-nowrap">Archive closed sessions older than:</label>
              <Input
                type="number"
                min="1"
                max="365"
                value={archiveDays}
                onChange={(e) => setArchiveDays(parseInt(e.target.value) || 7)}
                className="w-24"
              />
              <span className="text-sm text-gray-600">days</span>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                This will archive closed sessions that haven't been updated in the last {archiveDays} days.
                Archived sessions can still be accessed but won't appear in the main conversations list.
              </p>
            </div>

            <Button
              onClick={handleArchive}
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={isArchiving}
            >
              {isArchiving ? (
                <>
                  <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                  Archiving...
                </>
              ) : (
                <>
                  <Archive className="w-4 h-4 mr-2" />
                  Archive Sessions
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Security Notice */}
      <Card className="bg-gray-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-gray-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-700">Admin Access Only</p>
              <p className="text-xs text-gray-500">
                These maintenance operations are restricted to tenant administrators only.
                All actions are logged for security purposes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Maintenance;
